import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsProvisioned } from "@/hooks/useIsProvisioned";
import {
  getErpModules,
  findParentNavItem,
  findActiveSubItem,
  findActiveAppWithSubMenu,
  hasSubMenu,
  type NavigationItem,
} from "@/lib/config/navigationConfig";
import { useLineMessagingConfig } from "@/features/storefront/settings/useLineMessagingConfig";
import { useAuthStore } from "@/lib/store/authStore";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useSupportOnly } from "@/hooks/useSupportOnly";
import { ErpSidebarModuleRow } from "./ErpSidebarModuleRow";
import { ErpSidebarSubGroup } from "./ErpSidebarSubGroup";
import { NavIcon } from "./NavIcon";
import { StoreSelector } from "@/components/storefront/StoreSelector";

export function ErpSidebarContent() {
  const allModules = getErpModules();
  const location = useLocation();
  const isProvisioned = useIsProvisioned();
  const authMode = useAuthStore((s) => s.authMode);
  const tenantCompany = useAuthStore((s) => s.tenantCompany);
  const { status: lineMessagingStatus } = useLineMessagingConfig({
    enabled: authMode === "control_plane",
  });

  const { hasAccess } = useAppAccess();
  const supportOnly = useSupportOnly();

  const modules = allModules.filter((module) => {
    // Support-only tenants (e.g. heybay): only the Help Center is shown.
    if (supportOnly) return module.href === "/support";
    // Plan 1934: System Health is an Exogro-only pilot until it generalizes (F9)
    if ("requiresExogroTenant" in module && module.requiresExogroTenant) {
      return !!tenantCompany && tenantCompany.includes("574sn1poep");
    }
    if ("requiresLineOA" in module && module.requiresLineOA) {
      return lineMessagingStatus?.connected === true;
    }
    if ("appAccessKey" in module && module.appAccessKey) {
      return hasAccess(module.appAccessKey);
    }
    return true;
  });

  // Check if an app-rail item with a subMenu is active (e.g. Builder/สร้างเว็บ)
  const activeApp = findActiveAppWithSubMenu(location.pathname);

  const activeModule = findParentNavItem(location.pathname);
  const activeSubItem = findActiveSubItem(location.pathname);

  // Track which modules are expanded
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Auto-expand the active module
  useEffect(() => {
    if (activeModule && hasSubMenu(activeModule)) {
      setExpanded((prev) => {
        if (prev.has(activeModule.name)) return prev;
        const next = new Set(prev);
        next.add(activeModule.name);
        return next;
      });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = useCallback((name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // If an app-rail item with subMenu is active, show its menu instead
  if (activeApp && activeApp.subMenu) {
    return <AppSubMenuPanel app={activeApp} pathname={location.pathname} />;
  }

  return (
    <MenuPanel
      modules={modules}
      activeModule={activeModule ?? null}
      activeSubItem={activeSubItem}
      expanded={expanded}
      isProvisioned={isProvisioned}
      onToggleExpand={toggleExpand}
    />
  );
}

// ── Module names that go in the "system" group (top) ──────────
const SYSTEM_MODULES = new Set(["แดชบอร์ด", "ศูนย์ช่วยเหลือ", "ตั้งค่า"]);

// ── Menu panel — collapsible sections ─────────────────────────

interface MenuPanelProps {
  modules: NavigationItem[];
  activeModule: NavigationItem | null;
  activeSubItem: ReturnType<typeof findActiveSubItem>;
  expanded: Set<string>;
  isProvisioned: boolean;
  onToggleExpand: (name: string) => void;
}

function MenuPanel({
  modules,
  activeModule,
  activeSubItem,
  expanded,
  isProvisioned,
  onToggleExpand,
}: MenuPanelProps) {
  const [search, setSearch] = useState("");

  const sysModules = useMemo(
    () => modules.filter((m) => SYSTEM_MODULES.has(m.name)),
    [modules]
  );
  const bizModules = useMemo(
    () => modules.filter((m) => !SYSTEM_MODULES.has(m.name)),
    [modules]
  );

  const q = search.toLowerCase();
  const filteredSys = q ? sysModules.filter((m) => m.name.toLowerCase().includes(q)) : sysModules;
  const filteredBiz = q ? bizModules.filter((m) => m.name.toLowerCase().includes(q)) : bizModules;

  return (
    <div className="py-3 space-y-6">
      {/* Search */}
      <div className="px-3">
        <div className="flex items-center gap-2 h-9 px-3 rounded bg-gray-100/80 dark:bg-gray-800/80 border border-gray-200/30 dark:border-gray-700/30">
          <NavIcon icon={Search} className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={2} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 outline-none"
          />
        </div>
      </div>

      {/* System group */}
      {filteredSys.length > 0 && (
        <ModuleGroup
          modules={filteredSys}
          activeModule={activeModule}
          activeSubItem={activeSubItem}
          expanded={expanded}
          isProvisioned={isProvisioned}
          onToggleExpand={onToggleExpand}
        />
      )}

      {/* Business group */}
      {filteredBiz.length > 0 && (
        <ModuleGroup
          label="Business"
          modules={filteredBiz}
          activeModule={activeModule}
          activeSubItem={activeSubItem}
          expanded={expanded}
          isProvisioned={isProvisioned}
          onToggleExpand={onToggleExpand}
        />
      )}
    </div>
  );
}

// ── Module group with collapsible sub-menus ───────────────────

function ModuleGroup({
  modules,
  activeModule,
  activeSubItem,
  expanded,
  isProvisioned,
  onToggleExpand,
  label,
}: {
  modules: NavigationItem[];
  activeModule: NavigationItem | null;
  activeSubItem: ReturnType<typeof findActiveSubItem>;
  expanded: Set<string>;
  isProvisioned: boolean;
  onToggleExpand: (name: string) => void;
  label?: string;
}) {
  return (
    <div>
      {label && (
        <div className="text-[11px] font-medium text-[#0F0F10]/45 dark:text-gray-500 uppercase tracking-wider px-4 pb-1.5">
          {label}
        </div>
      )}
      <div className="px-2 space-y-1">
        {modules.map((module) => {
          const isActive = activeModule?.name === module.name;
          const canExpand = hasSubMenu(module);
          const isExpanded = expanded.has(module.name);

          return (
            <div key={module.name}>
              {/* Module row — if expandable, clicking toggles instead of navigating */}
              {canExpand ? (
                <ExpandableModuleRow
                  module={module}
                  isActive={isActive}
                  isExpanded={isExpanded}
                  isProvisioned={isProvisioned}
                  onToggle={() => onToggleExpand(module.name)}
                />
              ) : (
                <ErpSidebarModuleRow
                  module={module}
                  isActive={isActive}
                  isProvisioned={isProvisioned}
                  onDrillIn={() => {}}
                  insetCard
                />
              )}

              {/* Collapsible sub-menu */}
              {canExpand && isExpanded && module.subMenu && (
                <div className="ml-2 pl-3 border-l border-gray-200/40 dark:border-gray-700/40 mt-0.5 mb-1.5">
                  {module.subMenu.map((group) => (
                    <ErpSidebarSubGroup
                      key={group.title}
                      group={group}
                      activeSubItemHref={activeSubItem?.href}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── App sub-menu panel — shown when an app-rail item with subMenu is active ──

function AppSubMenuPanel({ app, pathname }: { app: NavigationItem; pathname: string }) {
  const isStorefront = pathname.startsWith("/storefront");
  return (
    <div className="py-3 space-y-4">
      {isStorefront && <StoreSelector />}
      <div className="px-2">
        {app.subMenu!.map((group) => (
          <ErpSidebarSubGroup
            key={group.title}
            group={group}
            activeSubItemHref={
              // Most-specific match wins: a parent route (e.g. "/fulfillment")
              // prefix-matches every child, so pick the longest matching href
              // instead of the first, otherwise the home item stays highlighted
              // on every sub-page.
              group.items
                .filter(
                  (item) => pathname === item.href || pathname.startsWith(item.href + "/")
                )
                .sort((a, b) => b.href.length - a.href.length)[0]?.href
            }
          />
        ))}
      </div>
    </div>
  );
}

// ── Expandable module row — chevron rotates on expand ─────────

function ExpandableModuleRow({
  module,
  isActive,
  isExpanded,
  isProvisioned,
  onToggle,
}: {
  module: NavigationItem;
  isActive: boolean;
  isExpanded: boolean;
  isProvisioned: boolean;
  onToggle: () => void;
}) {
  const disabled = module.requiresProvisioning && !isProvisioned;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2.5 h-[38px] px-2.5 rounded-sm w-full text-left transition-colors cursor-pointer",
        disabled
          ? "opacity-50 pointer-events-none"
          : isActive
            ? "bg-gray-100 dark:bg-gray-800"
            : "hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
      )}
    >
      <NavIcon
        icon={module.icon}
        className={cn(
          "w-[17px] h-[17px] shrink-0",
          isActive ? "text-[#0F0F10] dark:text-gray-100" : "text-[#0F0F10]/55 dark:text-gray-500"
        )}
      />
      <span
        className={cn(
          "text-[14px] truncate flex-1",
          isActive
            ? "text-[#0F0F10] dark:text-gray-100 font-medium"
            : "text-[#0F0F10]/60 dark:text-gray-400"
        )}
      >
        {module.name}
      </span>
      <NavIcon
        icon={ChevronDown}
        strokeWidth={2}
        className={cn(
          "w-3.5 h-3.5 shrink-0 transition-transform duration-200",
          isExpanded ? "rotate-180" : "",
          isActive ? "text-gray-900/40 dark:text-gray-100/40" : "text-gray-900/30 dark:text-gray-100/30"
        )}
      />
    </button>
  );
}
